import React, {forwardRef, useCallback, useLayoutEffect, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {SelectButton} from 'primereact/selectbutton';
import H5AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import './AudioPlayerWithSpeed.scss';

const PLAYBACK_RATES = [
    {value: 0.5, label: '0.5'},
    {value: 1, label: '1.0'},
    {value: 1.25, label: '1.25'},
    {value: 1.5, label: '1.5'},
    {value: 2, label: '2.0'},
    {value: 3, label: '3.0'},
] as const;

const STORAGE_KEY = 'audioPlaybackRate';

type PlaybackRate = typeof PLAYBACK_RATES[number]['value'];
type RateListener = (rate: PlaybackRate) => void;

const rateListeners = new Set<RateListener>();

const isPlaybackRate = (value: number): value is PlaybackRate => (
    PLAYBACK_RATES.some((rate) => rate.value === value)
);

const readStoredRate = (): PlaybackRate => {
    try {
        const value = Number(window.localStorage.getItem(STORAGE_KEY));
        if (isPlaybackRate(value)) {
            return value;
        }
    } catch {
        // localStorage can be unavailable in private contexts
    }

    return 1;
};

let currentPlaybackRate: PlaybackRate = typeof window === 'undefined' ? 1 : readStoredRate();

const subscribePlaybackRate = (listener: RateListener) => {
    rateListeners.add(listener);
    return () => {
        rateListeners.delete(listener);
    };
};

const publishPlaybackRate = (rate: PlaybackRate) => {
    currentPlaybackRate = rate;

    try {
        window.localStorage.setItem(STORAGE_KEY, String(rate));
    } catch {
        // Ignore storage failures and keep the in-memory rate.
    }

    rateListeners.forEach((listener) => listener(rate));
};

const applyPlaybackRate = (audio: HTMLAudioElement | null | undefined, rate: PlaybackRate) => {
    if (!audio) {
        return;
    }

    audio.playbackRate = rate;
    audio.preservesPitch = true;
    (audio as HTMLAudioElement & {webkitPreservesPitch?: boolean}).webkitPreservesPitch = true;
};

const usePlaybackRate = () => {
    const [rate, setRateState] = useState<PlaybackRate>(currentPlaybackRate);

    useEffect(() => subscribePlaybackRate(setRateState), []);

    const setRate = (nextRate: PlaybackRate) => {
        if (nextRate === currentPlaybackRate) {
            return;
        }

        publishPlaybackRate(nextRate);
    };

    return {rate, setRate};
};

type AudioPlayerProps = React.ComponentProps<typeof H5AudioPlayer> & {
    speedPlacement?: 'dialog-header' | 'player' | 'below';
};

const AudioPlayerWithSpeed = forwardRef<H5AudioPlayer, AudioPlayerProps>((props, ref) => {
    const innerRef = useRef<H5AudioPlayer | null>(null);
    const externalRef = useRef(ref);
    const anchorRef = useRef<HTMLDivElement | null>(null);
    const [dialogHeader, setDialogHeader] = useState<HTMLElement | null>(null);
    const {rate, setRate} = usePlaybackRate();
    const {
        header,
        className,
        onPlay,
        onCanPlay,
        onLoadedMetaData,
        onLoadedData,
        onLoadStart,
        src,
        speedPlacement = 'dialog-header',
        customAdditionalControls,
        ...rest
    } = props;

    externalRef.current = ref;

    const assignRef = useCallback((node: H5AudioPlayer | null) => {
        innerRef.current = node;
        applyPlaybackRate(node?.audio.current, currentPlaybackRate);

        const outerRef = externalRef.current;
        if (typeof outerRef === 'function') {
            outerRef(node);
        } else if (outerRef) {
            outerRef.current = node;
        }
    }, []);

    useEffect(() => {
        applyPlaybackRate(innerRef.current?.audio.current, rate);
    }, [rate, src]);

    useLayoutEffect(() => {
        if (speedPlacement === 'player') {
            setDialogHeader(null);
            return;
        }

        const dialog = anchorRef.current?.closest('.p-dialog');
        const headerElement = dialog?.querySelector(':scope > .p-dialog-header');
        setDialogHeader(headerElement instanceof HTMLElement ? headerElement : null);
    }, [speedPlacement]);

    const keepPlaybackRate = (event: Event) => {
        applyPlaybackRate(event.currentTarget as HTMLAudioElement | null, currentPlaybackRate);
    };

    const rateBelowPlayer = speedPlacement === 'below';
    const rateInPlayer = speedPlacement === 'player';
    const speedControl = (
        <div
            className="audio-playback-speed"
            onMouseDown={(event) => event.stopPropagation()}
        >
            <SelectButton
                value={rate}
                options={[...PLAYBACK_RATES]}
                optionLabel="label"
                optionValue="value"
                unselectable={false}
                aria-label="Швидкість відтворення"
                onChange={(event) => {
                    const nextRate = Number(event.value);
                    if (!isPlaybackRate(nextRate)) {
                        return;
                    }

                    setRate(nextRate);
                    applyPlaybackRate(innerRef.current?.audio.current, nextRate);
                }}
            />
        </div>
    );

    const showSpeedInPlayerHeader = Boolean(header) || speedPlacement === 'dialog-header' || rateInPlayer;

    return (
        <H5AudioPlayer
            {...rest}
            src={src}
            ref={assignRef}
            customAdditionalControls={rateBelowPlayer ? [speedControl] : customAdditionalControls}
            className={[
                'audio-player-with-speed',
                dialogHeader ? 'audio-player-with-speed--rate-in-dialog' : '',
                rateBelowPlayer ? 'audio-player-with-speed--rate-left' : '',
                className,
            ].filter(Boolean).join(' ')}
            header={showSpeedInPlayerHeader ? (
                <>
                    {header}
                    {speedPlacement === 'dialog-header' && <div ref={anchorRef} className="audio-playback-speed-anchor"/>}
                    {dialogHeader ? createPortal(speedControl, dialogHeader) : rateInPlayer ? speedControl : null}
                </>
            ) : undefined}
            onLoadStart={(event) => {
                keepPlaybackRate(event);
                onLoadStart?.(event);
            }}
            onLoadedMetaData={(event) => {
                keepPlaybackRate(event);
                onLoadedMetaData?.(event);
            }}
            onLoadedData={(event) => {
                keepPlaybackRate(event);
                onLoadedData?.(event);
            }}
            onCanPlay={(event) => {
                keepPlaybackRate(event);
                onCanPlay?.(event);
            }}
            onPlay={(event) => {
                keepPlaybackRate(event);
                onPlay?.(event);
            }}
        />
    );
});

AudioPlayerWithSpeed.displayName = 'AudioPlayerWithSpeed';

export default AudioPlayerWithSpeed;
